// @/components/reusable/ErrorDisplay.jsx

/**
 * ErrorDisplay Component
 * 
 * A standardized component for displaying error messages across the application.
 * Designed to match the visual style of InvalidTokenCard for consistent error handling.
 * 
 * Features:
 * - Configurable error title and message
 * - Primary and secondary action buttons
 * - Animated entrance transitions
 * - Consistent styling with design system
 * - Localization support through content hooks
 * - Flexible layout with proper spacing
 * - Customizable appearance through className prop
 * - Fallback to default content when none provided
 * - Graceful handling of optional actions
 * - Integrated with motion animations
 * 
 * @module components/reusable/ErrorDisplay
 */

import React from "react";
import * as motion from "motion/react-client"
import { INTRO } from '@/components/reusable/animations';
import { Button, ButtonGroup } from "@/components/reusable/button";
import { useContentInvitation } from '@/hooks/content/useContentInvitation';

/**
 * ErrorDisplay - Simplified error display component
 * 
 * @param {Object} props - Component props
 * @param {string} [props.title] - Error title text
 * @param {string} [props.message] - Error message text
 * @param {Object} [props.primaryAction] - Primary action configuration
 * @param {Function} props.primaryAction.onClick - Click handler for primary action
 * @param {string} [props.primaryAction.label] - Label for primary action button
 * @param {Object} [props.secondaryAction] - Secondary action configuration
 * @param {Function} props.secondaryAction.onClick - Click handler for secondary action
 * @param {string} [props.secondaryAction.label] - Label for secondary action button
 * @param {string} [props.className=""] - Additional CSS classes for styling
 * @returns {React.ReactElement} The error display component
 */
const ErrorDisplay = ({
  title,
  message,
  primaryAction,
  secondaryAction,
  className = ""
}) => {
  // Get localized content using the existing hook
  const content = useContentInvitation();

  return (
    <motion.div
      {...INTRO}
      className={`py-40 sm:py-0 mx-auto max-w-2xl text-center ${className}`}
    >
      <div className="space-y-8">
        <h1 className="text-rose-500">{title || content?.errorTitle}</h1>
        <h4 className="font-medium">
          {message || content?.errorDefaultMessage}
          {!message && content?.errorGuidance && ` ${content.errorGuidance}`}
        </h4>

        <ButtonGroup className="justify-center! mt-12!">
          {secondaryAction && (
            <Button
              variant="outline"
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label || content?.buttonGetHelp || "Get help"}
            </Button>
          )}

          {primaryAction && (
            <Button
              onClick={primaryAction.onClick}
            >
              {primaryAction.label || content?.buttonLogin || "Login"}
            </Button>
          )}
        </ButtonGroup>
      </div>
    </motion.div>
  );
};

export default ErrorDisplay;